import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Eye, Download, FileText, Image, FileSpreadsheet, File } from "lucide-react";
import ImageViewer from "./ImageViewer";
import { requiresUploadBlobAuth, fetchUploadBlob, openUploadInNewTab, downloadUploadFile } from "@/api/apiClient";

const getFileIcon = (fileName) => {
  const ext = fileName?.split('.').pop()?.toLowerCase();
  
  const iconMap = {
    pdf: FileText,
    doc: FileText,
    docx: FileText,
    txt: FileText,
    xls: FileSpreadsheet,
    xlsx: FileSpreadsheet,
    csv: FileSpreadsheet,
    jpg: Image,
    jpeg: Image,
    png: Image,
    gif: Image,
  };
  
  const IconComponent = iconMap[ext] || File;
  return IconComponent;
};

const isImage = (fileName) => {
  const ext = fileName?.split('.').pop()?.toLowerCase();
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
};

const isPDF = (fileName) => {
  const ext = fileName?.split('.').pop()?.toLowerCase();
  return ext === 'pdf';
};

export default function FileViewer({ fileUrl, fileName, trigger, compact = false }) {
  const FileIcon = getFileIcon(fileName);
  // Para imagens da nossa API, resolver blob URL para exibir (token não vai no <img src>)
  const [resolvedImageUrl, setResolvedImageUrl] = useState(fileUrl);
  const blobUrlRef = useRef(null);
  useEffect(() => {
    if (!fileUrl || !requiresUploadBlobAuth(fileUrl)) {
      setResolvedImageUrl(fileUrl);
      return;
    }
    if (!isImage(fileName)) {
      setResolvedImageUrl(fileUrl);
      return;
    }
    let cancelled = false;
    fetchUploadBlob(fileUrl)
      .then((blob) => {
        if (cancelled) return;
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = URL.createObjectURL(blob);
        setResolvedImageUrl(blobUrlRef.current);
      })
      .catch(() => {
        if (!cancelled) setResolvedImageUrl(fileUrl);
      });
    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [fileUrl, fileName]);

  const handleOpenFile = () => {
    openUploadInNewTab(fileUrl);
  };

  const handleDownload = () => {
    downloadUploadFile(fileUrl, fileName);
  };

  const actionButtonsClass = compact
    ? "flex items-center gap-2"
    : "flex flex-col gap-2 w-full";

  const buttonClass = compact ? "flex items-center gap-2" : "w-full";

  // Trigger customizado tem prioridade (PDF/imagem/outros)
  if (trigger) {
    if (isImage(fileName)) {
      return (
        <ImageViewer fileUrl={resolvedImageUrl} fileName={fileName} trigger={trigger} />
      );
    }
    return (
      <div onClick={handleOpenFile} className="cursor-pointer w-full">
        {trigger}
      </div>
    );
  }

  // Se for imagem sem trigger customizado
  if (isImage(fileName)) {
    return (
      <div className={actionButtonsClass}>
        <ImageViewer 
          imageUrl={resolvedImageUrl} 
          imageName={fileName}
          trigger={
            <Button
              variant="outline"
              size={compact ? "sm" : "default"}
              className={buttonClass}
            >
              <Eye className="w-4 h-4" />
              {!compact && "Visualizar"}
            </Button>
          }
        />
        <Button
          variant="outline"
          size={compact ? "sm" : "default"}
          onClick={handleDownload}
          className={buttonClass}
          title="Baixar arquivo"
        >
          <Download className="w-4 h-4" />
          {!compact && "Baixar"}
        </Button>
      </div>
    );
  }

  // Se for PDF
  if (isPDF(fileName)) {
    return (
      <div className={actionButtonsClass}>
        <Button
          variant="outline"
          size={compact ? "sm" : "default"}
          onClick={handleOpenFile}
          className={buttonClass}
          title="Visualizar PDF no navegador"
        >
          <Eye className="w-4 h-4" />
          {!compact && "Visualizar PDF"}
        </Button>
        <Button
          variant="outline"
          size={compact ? "sm" : "default"}
          onClick={handleDownload}
          className={buttonClass}
          title="Baixar arquivo"
        >
          <Download className="w-4 h-4" />
          {!compact && "Baixar"}
        </Button>
      </div>
    );
  }

  // Botões padrão para outros tipos de arquivo
  return (
    <div className={actionButtonsClass}>
      <Button
        variant="outline"
        size={compact ? "sm" : "default"}
        onClick={handleOpenFile}
        className={buttonClass}
        title="Abrir em nova aba"
      >
        <Eye className="w-4 h-4" />
        {!compact && "Abrir"}
      </Button>
      <Button
        variant="outline"
        size={compact ? "sm" : "default"}
        onClick={handleDownload}
        className={buttonClass}
        title="Baixar arquivo"
      >
        <Download className="w-4 h-4" />
        {!compact && "Baixar"}
      </Button>
    </div>
  );
}